import { useEffect, useState } from 'react';

import { getFeatureFlag, isFeatureFlagEnabled, onFeatureFlags } from '../utils/analytics';

export type FeatureFlagState<T> = {
  /** The resolved flag value, or `undefined` until the async read completes. */
  value: T | undefined;
  /** True until the first read resolves — hold/skeleton on this rather than
   * collapsing `undefined` to `false`, so the UI never assumes "off" mid-read. */
  isLoading: boolean;
};

// TODO: both PostHog SDKs already ship flag hooks that re-read on flag
// reload (posthog-react-native `useFeatureFlag`; posthog-js/react
// `useFeatureFlagEnabled` / `useFeatureFlagVariantKey`) — so the `onFeatureFlags`
// subscription below would come for free. We hand-roll these because the two
// platforms are wired asymmetrically: native mounts `PostHogProvider`, but web
// uses the `posthog-js` singleton with no `posthog-js/react` context. Consider
// adopting the SDK hooks instead (stand up `posthog-js/react`'s provider on web,
// reconcile the two hook APIs and the `{ value, isLoading }` shape) and deleting
// these wrappers + the manual reload subscription.
/**
 * Evaluate a boolean feature flag *on mount* — i.e. when the consuming screen is
 * actually rendered, not snapshotted globally at auth time. This sidesteps the
 * identity race of a once-at-auth resolution: by the time a screen mounts the
 * user is identified and their person properties are set, so the flag resolves
 * against the right person.
 *
 * Returns `{ value, isLoading }`. `value` is `undefined` while the read is in
 * flight; `isLoading` is true for that window. Callers should branch on
 * `isLoading` (render a default / loading) instead of treating the in-flight
 * `undefined` as `false`. For a one-off decision inside an async handler/action,
 * prefer awaiting `isFeatureFlagEnabled` directly at that point.
 *
 * The loading window (`value: undefined`, `isLoading: true`) applies on **mount /
 * key change** only. On a post-mount reload (e.g. PostHog re-evaluating after
 * `identify`) the previous `value` is retained while the new read is in flight and
 * `isLoading` stays `false`, so reload consumers never see a skeleton flash. This
 * contract is shared by {@link useFeatureFlagVariant}.
 */
export function useFeatureFlag(key: string): FeatureFlagState<boolean> {
  const [value, setValue] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // read() can run concurrently (initial call + onFeatureFlags reload callbacks),
    // and those promises may resolve out of order — so only the most recently
    // started read applies its result; results from superseded reads are dropped.
    let latest = 0;
    const read = () => {
      const seq = ++latest;
      void isFeatureFlagEnabled(key)
        .then((v) => {
          if (cancelled || seq !== latest) return;
          setValue(v);
          setIsLoading(false);
        })
        .catch(() => {
          // Defensive: the analytics util already swallows errors + returns false,
          // so this can't fire today — but a future re-throw must not strand the
          // loading hold at `isLoading: true` forever. Fail closed (flag off).
          if (cancelled || seq !== latest) return;
          setValue(false);
          setIsLoading(false);
        });
    };
    // Clear the prior key's value when starting a new read so `value` is never a
    // stale result while `isLoading` is true (honors the documented contract).
    setValue(undefined);
    setIsLoading(true);
    read();
    // Re-read when PostHog reloads flags — notably after `identify` re-evaluates
    // them for the authenticated user. Without this, a read that ran before
    // identify (e.g. at provider mount) would never update for the current user.
    const unsubscribe = onFeatureFlags(read);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [key]);

  return { value, isLoading };
}

/**
 * Variant counterpart of {@link useFeatureFlag} for multivariate flags — `value`
 * is the variant key, a boolean, or `undefined` while resolving. Pass the known
 * variant union as `T` (e.g. `useFeatureFlagVariant<'control' | 'test'>(key)`) so
 * the call-site comparison is type-checked instead of a loose string match.
 * Compare against the expected variant after checking `isLoading`.
 */
export function useFeatureFlagVariant<T extends string = string>(
  key: string
): FeatureFlagState<T | boolean> {
  const [value, setValue] = useState<T | boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // read() can run concurrently (initial call + onFeatureFlags reload callbacks),
    // and those promises may resolve out of order — so only the most recently
    // started read applies its result; results from superseded reads are dropped.
    let latest = 0;
    const read = () => {
      const seq = ++latest;
      void getFeatureFlag(key)
        .then((v) => {
          if (cancelled || seq !== latest) return;
          setValue(v as T | boolean | undefined);
          setIsLoading(false);
        })
        .catch(() => {
          // Defensive — see useFeatureFlag above. Fail closed (no variant).
          if (cancelled || seq !== latest) return;
          setValue(undefined);
          setIsLoading(false);
        });
    };
    // Clear the prior key's value when starting a new read so `value` is never a
    // stale result while `isLoading` is true (honors the documented contract).
    setValue(undefined);
    setIsLoading(true);
    read();
    // Re-read when PostHog reloads flags — notably after `identify` re-evaluates
    // them for the authenticated user. Without this, a read that ran before
    // identify (e.g. at provider mount) would never update for the current user.
    const unsubscribe = onFeatureFlags(read);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [key]);

  return { value, isLoading };
}
